// Wave UX-2.1 Item 1.13 (2026-05-23) — 외부 바이어 통화 환산 service + helper.
//
// 인터페이스:
//   formatKrwAmount(n, t)        — "1,500,000원" / "1,500,000 KRW" (locale suffix)
//   formatKrwAmountWithConvert(n, t, locale, rates)
//                                — "1,500,000원 (≈ $1,125)"  ko (보조 USD)
//                                — "1,500,000 KRW (≈ 4,135 AED)" en (보조 ar 통화)
//   useExchangeRates()           — { usd, aed, sar, rub, isStale } hook
//
// 환율 fetch: exchangerate-api.com free tier (key 불필요, 1,500/month)
// 캐시: localStorage 24h
// fallback rate (캐시 만료 + API 실패): hardcoded (2026-05-23 기준 근사)
//
// Locale → 보조 통화 매핑:
//   ko  → 환산 안 함 (KRW 기본)
//   en  → USD
//   ar  → AED (UAE)
//   ru  → RUB

import { useState, useEffect } from 'react';
import { API } from '../utils/api';

var STORAGE_KEY = 'autodex_buyer_exchange_rates';
var TTL_MS = 24 * 3600 * 1000; // 24h (client localStorage 캐시)
// 2026-05-27 — 외부 무료 환율 API 직접 호출(client-side, deprecated v4 + 브라우저 차단/rate-limit 빈번)
//   대신 서버 환율 엔드포인트 사용. 서버가 1회 fetch + 캐시 → 모든 바이어 공유.
var FX_ENDPOINT = API + '/buyer-app/fx';

// 2026-05-23 hardcoded fallback (KRW base — 1 KRW = X 외화).
// 운영 데이터 후 fetch 우선 — 본 값은 stale 시만 사용.
var FALLBACK = {
  usd: 0.00075,
  aed: 0.00276,
  sar: 0.00282,
  rub: 0.067,
};

var SUPPORTED_CURRENCIES = ['usd', 'aed', 'sar', 'rub'];

// Locale → 1차 보조 통화 매핑 (Flutter 정합)
var LOCALE_PRIMARY = {
  ko: null,    // 환산 안 함
  en: 'usd',
  ar: 'aed',
  ru: 'rub',
};

export function getPrimaryCurrency(locale) {
  return LOCALE_PRIMARY[locale] || null;
}

function loadCache() {
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) { return null; }
}

function saveCache(rates) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      rates: rates,
      fetchedAt: Date.now(),
    }));
  } catch (e) {}
}

function applyServerResponse(json) {
  // 서버 /buyer-app/fx 응답: { success, data: { rates:{usd,aed,sar,rub}, stale } }
  var d = json && json.data;
  var r = d && d.rates;
  if (!r || typeof r.usd !== 'number') return null;
  return {
    rates: {
      usd: r.usd,
      aed: (typeof r.aed === 'number') ? r.aed : FALLBACK.aed,
      sar: (typeof r.sar === 'number') ? r.sar : FALLBACK.sar,
      rub: (typeof r.rub === 'number') ? r.rub : FALLBACK.rub,
    },
    stale: !!d.stale,
  };
}

export function useExchangeRates() {
  var [state, setState] = useState(function() {
    var cached = loadCache();
    if (cached && cached.rates && (Date.now() - cached.fetchedAt) < TTL_MS) {
      return { rates: cached.rates, isStale: false };
    }
    return { rates: FALLBACK, isStale: true };
  });

  useEffect(function() {
    // 캐시 valid 면 fetch skip (TTL 안)
    var cached = loadCache();
    if (cached && cached.rates && (Date.now() - cached.fetchedAt) < TTL_MS) return;
    var aborted = false;
    fetch(FX_ENDPOINT, { method: 'GET' })
      .then(function(r){ return r.json(); })
      .then(function(json){
        if (aborted) return;
        var parsed = applyServerResponse(json);
        if (parsed) {
          saveCache(parsed.rates);
          // 서버가 외부 fetch 실패로 fallback 중이면 stale 표시 유지 (정직)
          setState({ rates: parsed.rates, isStale: parsed.stale });
        }
      })
      .catch(function(){
        // 실패 → fallback rate (이미 state 초기값) 유지
      });
    return function() { aborted = true; };
  }, []);

  return state;
}

// 천단위 콤마 정수 포맷
function fmtInt(n) {
  if (n == null || isNaN(n)) return '-';
  return Math.round(n).toLocaleString();
}

// "1,500,000원" / "1,500,000 KRW" (locale suffix)
export function formatKrwAmount(n, t) {
  if (n == null) return '-';
  var num = Number(n);
  if (isNaN(num)) return '-';
  return num.toLocaleString() + (t ? t('amountKrwSuffix') : '원');
}

// "1,500,000원 (≈ $1,125)" — locale 별 보조 통화 자동
// ko 는 보조 통화 없음 → formatKrwAmount 동일.
export function formatKrwAmountWithConvert(n, t, locale, rates) {
  var base = formatKrwAmount(n, t);
  if (!n || !rates) return base;
  var currency = getPrimaryCurrency(locale);
  if (!currency) return base;     // ko fallback
  var rate = rates[currency];
  if (!rate) return base;
  var converted = Number(n) * rate;
  var key = 'currencyApprox' + currency.charAt(0).toUpperCase() + currency.slice(1);
  var approxText = t(key, { amount: fmtInt(converted) });
  return base + ' (' + approxText + ')';
}

// "1,500,000원 (≈ $1,125)" — locale 무관 항상 USD 병기 (수출 거래 기준통화).
// 전체매물 마켓플레이스 가격 전용. ko 포함 모든 locale 에서 USD 노출 (Kyle 2026-05-26 요청 "₩ + USD 병기").
// rates.usd (KRW base → 1 KRW = X USD) 없으면 KRW 만.
export function formatKrwAmountWithUsd(n, t, rates) {
  var base = formatKrwAmount(n, t);
  if (!n || !rates || !rates.usd) return base;
  var converted = Number(n) * rates.usd;
  return base + ' (' + t('currencyApproxUsd', { amount: fmtInt(converted) }) + ')';
}

// 1 USD = N KRW (정수). rates.usd (1 KRW = X USD) 역수. 환율 표시용. 없으면 null.
export function getUsdKrwRate(rates) {
  if (!rates || !rates.usd) return null;
  return Math.round(1 / rates.usd);
}

export { SUPPORTED_CURRENCIES, FALLBACK };
